import React from 'react';
import { styled } from '@mui/material';
import { MuiModal } from 'ui/MuiModal';
import { ContentModule } from 'components/content/models/ContentModule';
import { ConfirmDialog } from 'ui/dialogs/ConfirmDialog';
import { CreateRuleResource, RuleResource } from 'components/rules/rules.types';
import { rulesApi } from 'components/rules/rulesApi';
import { RuleScheduledTaskNotificationStepper } from './RuleScheduledTaskNotificationStepper';
import { Location } from 'components/location/models/LocationModel';
import { Audience } from 'components/audience/models/segmentModel';
import { Perk } from 'components/loyalty/models/PerkModel';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { QueryParameter } from 'lib/HttpClient';
import { RuleScheduleTaskNotificationPrompt } from './RuleScheduledTaskNotificationPrompt';

interface UpdateRuleScheduledTaskNotificationModalProps {
    open: boolean;
    onClose?: () => void;
    onError: (message: string) => void;
    onSuccess: (rule: RuleResource) => void;
    rule: RuleResource;
    modules: ContentModule[];
    locations: Array<Location>;
    audiences: Array<Audience>;
    perks: Array<Perk>;
    adjustmentSchemes: Array<AdjustmentScheme>;
}

const StyledMuiModal = styled(MuiModal)(() => ({
    overflow: 'visible' // Needed for the dropdowns
}));

export const UpdateRuleScheduledTaskNotificationModal: React.FunctionComponent<
    UpdateRuleScheduledTaskNotificationModalProps
> = props => {
    const { open, onClose } = props;
    return (
        <StyledMuiModal open={open} onClose={onClose}>
            <UpdateRuleScheduledTaskNotificationModalComponent {...props} />
        </StyledMuiModal>
    );
};

const UpdateRuleScheduledTaskNotificationModalComponent: React.FC<
    UpdateRuleScheduledTaskNotificationModalProps
> = props => {
    const { onClose, onError, onSuccess, rule, modules, locations, perks, audiences, adjustmentSchemes } =
        props;

    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [ruleResource, setRuleResource] = React.useState<CreateRuleResource | null>(null);
    const [confirmPauseOpen, setConfirmPauseOpen] = React.useState(false);

    const onSubmit = React.useCallback(
        async (data: CreateRuleResource) => {
            if (!data.active) {
                setConfirmPauseOpen(true);
                setRuleResource(data);
                return;
            }
            const result = await rulesApi.update(rule._id, {
                body: data
            });

            if (!result.ok) {
                onError(result.body.message);
            } else {
                onSuccess(result.body);
            }
        },
        [onError, onSuccess, setConfirmPauseOpen, rule._id]
    );
    const handleOnClose = React.useCallback(() => {
        setOpenConfirm(true);
    }, [setOpenConfirm]);
    const handleDialogOnCancel = React.useCallback(() => {
        setOpenConfirm(false);
    }, [setOpenConfirm]);
    const handlePauseModalClose = React.useCallback(() => {
        setConfirmPauseOpen(false);
    }, [setConfirmPauseOpen]);

    const handlePauseConfirmation = React.useCallback(
        async (includeEffect: boolean) => {
            if (!ruleResource) {
                return;
            }
            const queryParameters: QueryParameter[] = [];
            queryParameters.push({
                key: 'includeEffect',
                value: includeEffect
            });
            const result = await rulesApi.update(rule._id, {
                body: ruleResource,
                queryParameters
            });

            setConfirmPauseOpen(false);
            if (!result.ok) {
                onError(result.body.message);
            } else {
                onSuccess(result.body);
            }
        },
        [ruleResource, onError, onSuccess, setConfirmPauseOpen, rule._id]
    );

    const handlePauseSubmitWithEffect = React.useCallback(() => {
        handlePauseConfirmation(true);
    }, [handlePauseConfirmation]);

    const handlePauseSubmitWithoutEffect = React.useCallback(() => {
        handlePauseConfirmation(false);
    }, [handlePauseConfirmation]);

    return (
        <>
            <StyledMuiModal open={true} onClose={handleOnClose}>
                <RuleScheduledTaskNotificationStepper
                    initialData={rule}
                    modules={modules}
                    onClose={handleOnClose}
                    onSubmit={onSubmit}
                    locations={locations}
                    perks={perks}
                    adjustmentSchemes={adjustmentSchemes}
                    audiences={audiences}
                />
            </StyledMuiModal>
            <ConfirmDialog
                open={openConfirm}
                title="Hold On!"
                content="Are you sure you want to cancel this triggered notification update process? All changes you made will be lost."
                onCancel={handleDialogOnCancel}
                onConfirm={onClose}
                cancelLabel="No, don't cancel"
                confirmLabel="Yes, cancel"
            />
            <RuleScheduleTaskNotificationPrompt
                open={confirmPauseOpen}
                title="Confirmation Required"
                content="Would you also like to delete all pending notifications that were scheduled with this campaign?
                Please be aware, once deleted, there is no way to recover them."
                onCancel={handlePauseSubmitWithoutEffect}
                onConfirm={handlePauseSubmitWithEffect}
                showClose={false}
                onClose={handlePauseModalClose}
                cancelLabel="No, keep"
                confirmLabel="Yes, delete"
            />
        </>
    );
};
